import {
  ClockCircleOutlined,
  FileTextOutlined,
  HomeOutlined,
  LaptopOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { Badge, Button, List, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BookingType, Language } from '@/models';
import { Resource } from '@/models/resource.model';
import { ROUTE_CONSTANTS } from '@/routes';
import { DATETIME_WITHOUT_DAY_FORMAT } from '@/utils';
import { formatResourceName, showGender } from '@/utils/resourceHelper';

type ResourceListProps = {
  header?: ReactNode;
  footer?: ReactNode;
  resources?: Resource[];
  isLoading?: boolean;
  onRemove?: (id: string) => void;
};

const { Text } = Typography;

export const ResourceList = ({
  header,
  footer,
  resources,
  isLoading,
  onRemove,
}: ResourceListProps) => {
  const { t } = useTranslation();

  return (
    <List
      size='small'
      header={header}
      footer={footer}
      itemLayout='horizontal'
      dataSource={resources}
      loading={isLoading}
      renderItem={(item) => (
        <List.Item
          actions={[
            <Link key={item.id} to={`/admin/${ROUTE_CONSTANTS.RESOURCES_PATH}/${item.id}`}>
              {t('common:show')}
            </Link>,
            onRemove ? (
              <Button
                type='link'
                danger
                key={`remove-${item.id}`}
                onClick={() => onRemove(item.id)}
              >
                {t('common:remove')}
              </Button>
            ) : null,
          ]}
        >
          <List.Item.Meta
            avatar={item.hasMissingInformation ? <Badge status='error' /> : undefined}
            title={
              <Space orientation='horizontal' size='small' separator='-'>
                {`${formatResourceName(item.firstname, item.lastname, 'formal')}`}
                {item.number}
                {item.email}
                {showGender(item.gender)}
                {item.isArchived && <Tag color='red'>{t('common:archived')}</Tag>}
                {item.lastActivity ? (
                  <Text type='secondary' style={{ fontSize: '12px' }}>
                    <ClockCircleOutlined />
                    &nbsp;
                    {dayjs().diff(dayjs(item.lastActivity), 'day') > 0
                      ? dayjs(item.lastActivity).format(DATETIME_WITHOUT_DAY_FORMAT)
                      : dayjs(item.lastActivity).fromNow()}
                  </Text>
                ) : undefined}
              </Space>
            }
            description={
              <ResourceListItemContent
                bookingTypes={item.bookingTypes ?? []}
                languages={item.languages ?? []}
              />
            }
          />
        </List.Item>
      )}
    />
  );
};

const ResourceListItemContent = ({
  bookingTypes,
  languages,
}: {
  bookingTypes: BookingType[];
  languages: Language[];
}) => {
  const bookingTypeIcons: Record<BookingType, { icon: typeof HomeOutlined; rotate?: number }> = {
    [BookingType.ONSITE]: { icon: HomeOutlined },
    [BookingType.REMOTE]: { icon: LaptopOutlined },
    [BookingType.PHONE]: { icon: PhoneOutlined, rotate: 90 },
    [BookingType.DOCUMENT]: { icon: FileTextOutlined },
  };

  return (
    <Space size='small' separator='-'>
      <Space size={0}>
        {bookingTypes.map((type) => {
          const config = bookingTypeIcons[type];
          if (!config) return null;
          const Icon = config.icon;
          return (
            <Tag key={type}>
              <Icon style={{ rotate: config.rotate ? `${config.rotate}deg` : undefined }} />
            </Tag>
          );
        })}
      </Space>
      <Space size={0}>
        {languages.map((language: Language) => (
          <Tag key={language.id}>{language.name}</Tag>
        ))}
      </Space>
    </Space>
  );
};
